/**
 * BrandSelector Component
 *
 * Combobox with search for choosing a brand (manufacturer).
 * Used in Product Overview/Edit and Image Generation.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import { Combobox, Label, Badge } from '@/components/v2/ui';
import { useBrands } from '@/hooks/useBrands';
import type { BrandSource } from '@/types/brands';

interface BrandSelectorProps {
  value?: string | null;
  onChange: (brandId: string | null) => void;
  required?: boolean;
  label?: string;
  helpText?: string;
  disabled?: boolean;
}

const SOURCE_BADGE_VARIANT: Record<BrandSource, 'default' | 'primary' | 'success' | 'secondary'> = {
  manual: 'default',
  jtl: 'primary',
  shopify: 'success',
  amazon: 'secondary',
  ebay: 'secondary',
};

export function BrandSelector({
  value,
  onChange,
  required = false,
  label = 'Brand',
  helpText = 'Hersteller / Marke des Produkts',
  disabled = false,
}: BrandSelectorProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useBrands();

  const brands = data || [];

  const handleValueChange = (newValue: string) => {
    // "none" value means unset (null)
    onChange(newValue === 'none' ? null : newValue);
  };

  // Build options for Combobox
  const options = [
    // "Keine Brand" option
    {
      value: 'none',
      label: 'Keine Brand',
    },
    // Brand options
    ...brands.map((brand) => ({
      value: brand.id,
      label: brand.name,
      // Store brand data for rendering
      brand: brand,
    })),
  ];

  // Custom render function for option content (with colors + badge)
  const renderOption = (option: typeof options[0]) => {
    if (option.value === 'none') {
      return (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          <span className="text-neutral-400">Keine Brand</span>
        </div>
      );
    }

    const brand = 'brand' in option ? option.brand : null;
    if (!brand) return option.label;

    return (
      <div className="flex items-center gap-2">
        {/* Brand Colors Preview */}
        {brand.brand_colors.length > 0 && (
          <div className="flex gap-0.5">
            {brand.brand_colors.slice(0, 2).map((color: string, i: number) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full border border-neutral-200 dark:border-neutral-700"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        )}
        {brand.brand_colors.length === 0 && (
          <Package className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        )}
        <span>{brand.name}</span>
        <Badge variant={SOURCE_BADGE_VARIANT[brand.source as BrandSource]} className="text-xs">
          {brand.source}
        </Badge>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label>
          {label} {required && <span className="text-red-600">*</span>}
        </Label>
      )}

      <Combobox
        placeholder={t('brands.chooseBrand')}
        searchPlaceholder="Brand suchen..."
        emptyText={isLoading ? 'Brands laden...' : 'Keine Brands gefunden'}
        options={options}
        value={value || 'none'}
        onValueChange={handleValueChange}
        disabled={disabled || isLoading}
        renderOption={renderOption}
      />

      {helpText && <p className="text-sm text-neutral-500 dark:text-neutral-400">{helpText}</p>}
    </div>
  );
}
